import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { productService, Product, ProductCategory, ProductListQuery } from '../services/productService';
import { RootState } from '../../../shared/store/store';

interface ProductState {
    list: Product[];
    total: number;
    page: number;
    limit: number;
    current: Product | null;
    categories: ProductCategory[];
    loading: boolean;
    error: string | null;
}

const initialState: ProductState = {
    list: [],
    total: 0,
    page: 1,
    limit: 10,
    current: null,
    categories: [],
    loading: false,
    error: null,
};

function getToken(state: RootState): string {
    return state.auth.accessToken ?? '';
}

export const fetchProducts = createAsyncThunk(
    'products/fetchAll',
    async (query: ProductListQuery, { getState }) => {
        const token = getToken(getState() as RootState);
        const { data } = await productService.getAll(query, token);
        return data;
    }
);

export const fetchCategories = createAsyncThunk(
    'products/fetchCategories',
    async (_, { getState }) => {
        const token = getToken(getState() as RootState);
        const { data } = await productService.getCategories(token);
        return data.categories;
    }
);

export const fetchProductById = createAsyncThunk(
    'products/fetchById',
    async (id: string, { getState }) => {
        const token = getToken(getState() as RootState);
        const { data } = await productService.getById(id, token);
        return data.product;
    }
);

export const createProduct = createAsyncThunk(
    'products/create',
    async (formData: FormData, { getState }) => {
        const token = getToken(getState() as RootState);
        const { data } = await productService.create(formData, token);
        return data.product;
    }
);

export const updateProduct = createAsyncThunk(
    'products/update',
    async ({ id, formData }: { id: string; formData: FormData }, { getState }) => {
        const token = getToken(getState() as RootState);
        const { data } = await productService.update(id, formData, token);
        return data.product;
    }
);

export const deleteProduct = createAsyncThunk(
    'products/delete',
    async (id: string, { getState }) => {
        const token = getToken(getState() as RootState);
        await productService.remove(id, token);
        return id;
    }
);

const productSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        clearProductError: (state) => { state.error = null; },
        clearCurrentProduct: (state) => { state.current = null; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.list    = action.payload.products;
                state.total   = action.payload.total;
                state.page    = action.payload.page;
                state.limit   = action.payload.limit;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.error.message ?? 'Failed to fetch products';
            })

            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.categories = action.payload;
            })

            .addCase(fetchProductById.pending, (state) => { state.loading = true; })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.loading = false;
                state.current = action.payload;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.error.message ?? 'Failed to fetch product';
            })

            .addCase(createProduct.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(createProduct.fulfilled, (state) => { state.loading = false; })
            .addCase(createProduct.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.error.message ?? 'Failed to create product';
            })

            .addCase(updateProduct.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(updateProduct.fulfilled, (state) => { state.loading = false; })
            .addCase(updateProduct.rejected, (state, action) => {
                state.loading = false;
                state.error   = action.error.message ?? 'Failed to update product';
            })

            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.list  = state.list.filter(p => p._id !== action.payload);
                state.total = Math.max(0, state.total - 1);
            });
    },
});

export const { clearProductError, clearCurrentProduct } = productSlice.actions;
export default productSlice.reducer;
